import { io, Socket } from "socket.io-client";
import { getAuthContext } from "./authContext";

let socket: Socket | null = null;
let socketKey: string | null = null;

/** WebSocket origin for Socket.IO (same host as REST API in production). */
export function resolveSocketOrigin(apiBaseUrl: string): string {
  try {
    const override =
      typeof localStorage !== "undefined" ? localStorage.getItem("juricloud_socket_url")?.trim() : "";
    if (override) {
      return override.replace(/\/$/, "");
    }
  } catch {
    /* ignore */
  }
  const base = apiBaseUrl.replace(/\/$/, "");
  if (base.startsWith("/")) {
    return "wss://beta.api.juricloud.co";
  }
  try {
    const u = new URL(base.startsWith("http") ? base : `https://${base}`);
    const wsProto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProto}//${u.host}`;
  } catch {
    return "wss://beta.api.juricloud.co";
  }
}

export function getChatSocket(): Socket {
  const auth = getAuthContext();
  const origin = resolveSocketOrigin(auth.baseUrl);
  const key = `${origin}|${auth.accessToken}`;
  if (socket && socketKey === key) {
    return socket;
  }
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  socketKey = key;
  socket = io(`${origin}/ws`, {
    transports: ["websocket"],
    auth: { token: auth.accessToken },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelayMax: 5000,
  });
  return socket;
}

export function disconnectChatSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  socketKey = null;
}

export function waitForSocketConnect(sock: Socket, ms = 20000): Promise<void> {
  if (sock.connected) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      sock.off("connect", onOk);
      sock.off("connect_error", onErr);
      reject(new Error("Socket connection timed out."));
    }, ms);
    const onOk = () => {
      window.clearTimeout(timer);
      sock.off("connect_error", onErr);
      resolve();
    };
    const onErr = (err: Error) => {
      window.clearTimeout(timer);
      sock.off("connect", onOk);
      reject(err);
    };
    sock.once("connect", onOk);
    sock.once("connect_error", onErr);
  });
}

/** Normalize Socket.IO payload (some servers wrap as `[eventName, data]`). */
export function unwrapSocketPayload(raw: unknown): any {
  return Array.isArray(raw) ? raw[1] : raw;
}
